/**
 * Source View Component
 *
 * Implements:
 * - REQ-013: Proof Tree Visualization (source links)
 *
 * Displays Spindle Lisp source with syntax highlighting and line numbers.
 * Uses CSS classes from index.css for syntax highlighting colors.
 */

import { useState, useRef, useCallback } from "react";

interface SourceViewProps {
  source: string;
  highlightLines?: number[];
  onLineClick?: (lineNumber: number) => void;
}

interface Section {
  start: number;
  end: number;
  collapsed: boolean;
}

export default function SourceView({ source, highlightLines = [], onLineClick }: SourceViewProps) {
  const [selectedLine, setSelectedLine] = useState<number | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<number>>(new Set());
  const lines = source.split("\n");
  const sectionsRef = useRef<Map<number, Section>>(new Map());

  // Detect sections (lines starting with ";")
  const detectSections = useCallback(() => {
    const sections = new Map<number, Section>();
    let currentSection: Section | null = null;

    lines.forEach((line, i) => {
      const trimmed = line.trim();
      if (trimmed.startsWith(";") && !trimmed.startsWith(";;")) {
        if (currentSection) {
          currentSection.end = i - 1;
        }
        currentSection = { start: i, end: lines.length - 1, collapsed: false };
        sections.set(i, currentSection);
      }
    });

    sectionsRef.current = sections;
    return sections;
  }, [lines]);

  // Initialize sections
  if (sectionsRef.current.size === 0 && lines.length > 0) {
    detectSections();
  }

  const handleLineClick = (lineNumber: number) => {
    setSelectedLine(lineNumber);
    onLineClick?.(lineNumber);
  };

  const toggleSection = (sectionStart: number) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionStart)) {
        next.delete(sectionStart);
      } else {
        next.add(sectionStart);
      }
      return next;
    });
  };

  const isLineVisible = (lineIndex: number): boolean => {
    for (const [start, section] of sectionsRef.current) {
      if (collapsedSections.has(start) && lineIndex > start && lineIndex <= section.end) {
        return false;
      }
    }
    return true;
  };

  const isSectionHeader = (lineIndex: number): boolean => {
    return sectionsRef.current.has(lineIndex);
  };

  return (
    <div className="code-editor">
      {lines.map((line, i) => {
        if (!isLineVisible(i)) return null;

        const lineNum = i + 1;
        const isSelected = selectedLine === lineNum;
        const isHighlighted = highlightLines.includes(lineNum);
        const isHeader = isSectionHeader(i);
        const isCollapsed = collapsedSections.has(i);

        let bgClass = "";
        if (isSelected) bgClass = "bg-primary/10";
        else if (isHighlighted) bgClass = "bg-warning/10";

        // Check for special line backgrounds based on content
        if (line.includes("except") || line.includes("blocked")) {
          bgClass = "bg-warning/5";
        } else if (line.includes("claims") && line.includes("coder")) {
          bgClass = "bg-secondary/5";
        } else if (line.includes("claims") && line.includes("security")) {
          bgClass = "bg-accent/5";
        }

        return (
          <div
            key={i}
            className={`line ${bgClass} ${isHeader ? "section-header" : ""}`}
            onClick={() => isHeader ? toggleSection(i) : handleLineClick(lineNum)}
          >
            <span className="ln">{lineNum}</span>
            {isHeader && (
              <span className={`arrow ${isCollapsed ? "collapsed" : ""}`}>
                {isCollapsed ? "▸" : "▾"}
              </span>
            )}{" "}
            {highlightSyntax(line)}
          </div>
        );
      })}
    </div>
  );
}

function highlightSyntax(line: string): React.ReactNode {
  const tokens: React.ReactNode[] = [];
  let remaining = line;
  let key = 0;

  while (remaining.length > 0) {
    // Comments
    if (remaining.startsWith(";")) {
      tokens.push(
        <span key={key++} className="cm">
          {remaining}
        </span>
      );
      break;
    }

    // #lang directive
    if (remaining.startsWith("#lang")) {
      const match = remaining.match(/^#lang\s+\S+/);
      if (match) {
        tokens.push(
          <span key={key++} className="kw">
            {match[0]}
          </span>
        );
        remaining = remaining.slice(match[0].length);
        continue;
      }
    }

    // Keywords
    const keywordMatch = remaining.match(/^(given|normally|except|always|prefer|claims|meta|import|provide)\b/);
    if (keywordMatch) {
      tokens.push(
        <span key={key++} className="kw">
          {keywordMatch[0]}
        </span>
      );
      remaining = remaining.slice(keywordMatch[0].length);
      continue;
    }

    // Functions/operators
    const funcMatch = remaining.match(/^(and|or|not)\b/);
    if (funcMatch) {
      tokens.push(
        <span key={key++} className="fm">
          {funcMatch[0]}
        </span>
      );
      remaining = remaining.slice(funcMatch[0].length);
      continue;
    }

    // Strings
    const stringMatch = remaining.match(/^"[^"]*"/);
    if (stringMatch) {
      tokens.push(
        <span key={key++} className="st">
          {stringMatch[0]}
        </span>
      );
      remaining = remaining.slice(stringMatch[0].length);
      continue;
    }

    // Metadata keywords (:at, :note, etc)
    const metaMatch = remaining.match(/^:[a-z]+/);
    if (metaMatch) {
      tokens.push(
        <span key={key++} className="mk">
          {metaMatch[0]}
        </span>
      );
      remaining = remaining.slice(metaMatch[0].length);
      continue;
    }

    // Labels/identifiers
    const labelMatch = remaining.match(/^[a-z][a-z0-9-]*/);
    if (labelMatch) {
      const word = labelMatch[0];
      // Rule names (r1, r2, d1, a1, etc.)
      if (word.match(/^[rda]\d+$/)) {
        tokens.push(
          <span key={key++} className="lb">
            {word}
          </span>
        );
      }
      // Literals (task-*, ready-*, etc.)
      else if (word.match(/^(task|ready|completed|blocked|claimed|assign|deps-met|no-deps|agent|capability|done)-/)) {
        tokens.push(
          <span key={key++} className="lt">
            {word}
          </span>
        );
      }
      // Other identifiers
      else {
        tokens.push(<span key={key++}>{word}</span>);
      }
      remaining = remaining.slice(word.length);
      continue;
    }

    // Parentheses
    if (remaining[0] === "(" || remaining[0] === ")") {
      tokens.push(
        <span key={key++} className="pn">
          {remaining[0]}
        </span>
      );
      remaining = remaining.slice(1);
      continue;
    }

    // Default: single character
    tokens.push(<span key={key++}>{remaining[0]}</span>);
    remaining = remaining.slice(1);
  }

  return tokens.length > 0 ? tokens : " ";
}
